import React, { FC, memo, useEffect, useState, useTransition, CSSProperties } from 'react';
import classes from './styles.module.css';
import { FaMinus, FaPlus } from "react-icons/fa6";
import { useDispatch, useSelector } from 'react-redux';
import { useCartActionMutation } from 'src/store/api/cart/catsApiSlice';
import { isAuthSelector } from 'src/store/reducers/auth';
import { cartItemQty } from 'src/store/reducers/cart';
import { storageUpdateCartItemQty } from 'src/utils/cart';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface QtyInputType {
    qty: number;
    itemSlug: string;
    maxQty?: number;
    inputStyle?: string | CSSProperties;
    conStyle?: string | CSSProperties;
    inputExtraClassName?: string; // ✅ new
    conExtraClassName?: string;   // ✅ new
}

const QtyInput: FC<QtyInputType> = ({
    qty,
    itemSlug,
    maxQty = 10000,
    inputStyle,
    conStyle,
    inputExtraClassName = "",
    conExtraClassName = ""
}) => {
    const { t } = useTranslation("components");
    const dispatch = useDispatch();
    const [inputValue, setInputValue] = useState(qty.toString());
    const [isPending, startTransition] = useTransition();
    const [sendCartAction] = useCartActionMutation();
    const isAuth = useSelector(isAuthSelector);

    const TOAST_ID = "qty-limit";

    const showLimitToast = (message: string) => {
        if (toast.isActive(TOAST_ID)) {
            toast.update(TOAST_ID, {
                render: message,
                type: "warning",
                autoClose: 2000,
            });
        } else {
            toast.warning(message, { toastId: TOAST_ID, autoClose: 2000 });
        }
    };

    const updateQty = (newQty: number) => {
        startTransition(() => {
            dispatch(cartItemQty({ slug: itemSlug, qty: newQty }));
            storageUpdateCartItemQty(itemSlug, newQty);
            if (isAuth) {
                sendCartAction({ action: "update", slug: itemSlug, qty: newQty });
            }
        });
    };

    const onChangeHandle = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;

        if (/^\d*$/.test(value)) {
            if (value === "") value = "1";
            let parsedQty = parseInt(value, 10);
            if (parsedQty < 1) parsedQty = 1;

            if (parsedQty > maxQty) {
                showLimitToast(t("qty_input.error_msg", { maxQty }));
                setInputValue(maxQty.toString());
                updateQty(maxQty);
                return;
            }

            setInputValue(parsedQty.toString());
            updateQty(parsedQty);
        }
    };

    const onIncreaseHandle = () => {
        if (qty < maxQty) {
            updateQty(qty + 1);
        } else {
            showLimitToast(t("qty_input.error_msg", { maxQty }));
            setInputValue(maxQty.toString());
        }
    };

    const onDecreaseHandle = () => {
        if (qty > 1) {
            updateQty(qty - 1);
        }
    };

    useEffect(() => {
        setInputValue(qty.toString());
    }, [qty]);

    return (
        <div
            className={`${classes.qty__con} ${conExtraClassName}`}
            style={typeof conStyle !== 'string' ? conStyle : undefined}
        >
            <div className={classes.input__con}>
                <button
                    title={t("qty_input.increase") || "Increase quantity"}
                    onClick={onIncreaseHandle}
                    disabled={qty >= maxQty}
                >
                    <FaPlus size={16} />
                </button>

                <input
                    onWheel={(e) => e.currentTarget.blur()}
                    min={1}
                    max={maxQty}
                    type='number'
                    name="qty"
                    value={inputValue}
                    onChange={onChangeHandle}
                    className={`${typeof inputStyle === 'string' ? inputStyle : classes.input} ${inputExtraClassName}`}
                    style={typeof inputStyle !== 'string' ? inputStyle : undefined}
                />

                <button
                    title={t("qty_input.decrease") || "Decrease quantity"}
                    onClick={onDecreaseHandle}
                    disabled={qty <= 1}
                >
                    <FaMinus size={16} />
                </button>
            </div>
        </div>
    );
};

export default memo(QtyInput);
